import { useUserContext } from '@/api/hooks/useUserContext';
import { AppVersionFooter } from '@/components/AppVersionFooter';
import { CompanyCredit } from '@/components/CompanyCredit';
import RestaurantSwitcherSheet from '@/components/RestaurantSwitcherSheet';
import { SettingIcon } from '@/components/SettingIcon';
import { useRestaurantOpenStatus } from '@/features/opening-hours/hooks/useRestaurantOpenStatus';
import { SettingsHealthBanner } from '@/features/settings/components/SettingsHealthBanner';
import {
  useSettingsHealth,
  type SettingsHealthIssue,
} from '@/features/settings/hooks/useSettingsHealth';
import { featureFlags } from '@/featureFlags';
import { useAbility } from '@/hooks/useAbility';
import { canInScope } from '@/lib/ability';
import { InboxSheet } from '@/features/notifications/components/InboxSheet';
import { useUnreadCount } from '@/features/notifications/hooks/useInbox';
import { usePaymentProviders } from '@/features/payments/hooks/usePaymentProviders';
import { unregisterPush } from '@/features/notifications/hooks/usePushRegistration';
import { navigateMain } from '@/utils/navigateMain';
import { useMediaQuery } from '@/utils/useMediaQuery';
import { isAnalyticsOptedOut, setAnalyticsOptOut } from '@/lib/analytics/umami-client';
import { mediaSrc } from '@/lib/media-url';
import { LOCALES, useI18nStore, useT } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useUiStore } from '@/stores/ui.store';
import {
  BlockTitle,
  Link as F7Link,
  List,
  ListItem,
  Navbar,
  Page,
  View,
  f7,
} from 'framework7-react';
import { useEffect, useMemo, useRef, useState } from 'react';

// The nested settings content view: a plain F7 view (NOT master-detail) so its
// pages push/slide reliably, like Reservations. Each settings nav row loads its
// page in here; drill-ins (e.g. "+ new holiday") push on top with a Back button.
const DETAIL_VIEW_ID = 'view-settings-detail';

// First page shown when Settings opens (so the content pane is never empty).
const DEFAULT_DETAIL = '/settings/reservation/setup';

// Detail links, longest-overlap aware: resolves which nav row is "active" by
// longest-prefix match, so /settings/reservation/shifts highlights "Capacity",
// not "Table Plan".
const DETAIL_LINKS = [
  '/settings/reservation/setup',
  '/settings/reservation/shifts',
  '/settings/reservation/hours',
  '/settings/reservation',
  '/settings/orders/hours',
  '/settings/orders/shifts',
  '/settings/orders',
  '/settings/payments',
  '/settings/labels',
  '/settings/loyalty',
  '/settings/gift-cards',
  '/settings/staff',
  '/settings/table-ordering',
  '/settings/printers',
  '/settings/notifications',
  '/settings/mail',
  '/settings/profile',
  '/settings/restaurant',
  '/settings/branding',
  '/settings/storefront-link',
  '/settings/opening-hours',
  '/settings/holidays',
];

function SettingsPage() {
  const t = useT();
  const email = useAuthStore((s) => s.email);
  const logout = useAuthStore((s) => s.logout);
  const clearRestaurant = useRestaurantStore((s) => s.clear);
  const restaurantConfig = useRestaurantStore((s) => s.restaurantConfig);
  const storeContext = useRestaurantStore((s) => s.userContext);
  const selectedFranchiseId = useRestaurantStore((s) => s.selectedFranchiseId);
  const selectedRestaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  // Prefer freshly-fetched context, fall back to the store's bootstrap copy (it
  // isn't persisted, so it's null right after a reload until the query lands).
  const { data: ctx } = useUserContext();
  const ability = useAbility();
  const scope = { restaurantId: selectedRestaurantId, franchiseId: selectedFranchiseId };
  const canManageOrderSetup = canInScope(ability, 'manage', 'OrderTypeConfig', scope);
  const canManageOrderShifts = canInScope(ability, 'manage', 'OrderShift', scope);
  const canManageOpeningHours = canInScope(ability, 'manage', 'OpeningHours', scope);
  const canManagePurge = canInScope(ability, 'manage', 'PurgeRun', scope);
  const { locale, setLocale } = useI18nStore();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // The row that opened the switcher - anchors its popover on wide viewports.
  const switcherAnchorRef = useRef<HTMLElement | null>(null);
  // The language row's globe icon - anchors the language action menu to the left.
  const langAnchorRef = useRef<HTMLElement | null>(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const { data: unread } = useUnreadCount();
  const unreadCount = unread?.count ?? 0;

  // Which payment mode the storefront transacts in right now — surfaced as a pill
  // on the Payments row so the operator sees "Sandbox" (red = not taking real
  // money) or "Live" (green) without opening the page.
  const { data: paymentProviders } = usePaymentProviders();
  const activePayMode = paymentProviders?.find((p) => p.isActive)?.mode ?? null;

  // Desktop = full-page shell (left nav + nested content view). Mobile = a tab
  // showing this list, sub-pages pushed on the tab.
  const isDesktopLayout = useMediaQuery('(min-width: 768px)');
  const [activePath, setActivePath] = useState(DEFAULT_DETAIL);
  // Show the header's liquid-glass material only once the nav list is scrolled
  // under it (transparent at the top), like an iOS navbar.
  const [navScrolled, setNavScrolled] = useState(false);
  // Settings search, docked at the bottom of the desktop left rail. The nav is
  // hand-built JSX (not data-driven), so it's filtered in the DOM (effect below,
  // after activeLink is defined).
  const [settingsQuery, setSettingsQuery] = useState('');
  const navScrollRef = useRef<HTMLDivElement>(null);

  // Keep the active-row highlight in sync with the nested settings router on
  // drill-in/back. No URL involvement - F7 owns navigation.
  useEffect(() => {
    if (!isDesktopLayout) return;
    let cancelled = false;
    let detach = () => {};
    const attach = () => {
      if (cancelled) return;
      const router = f7.views.get(`#${DETAIL_VIEW_ID}`)?.router;
      if (!router) {
        window.setTimeout(attach, 50);
        return;
      }
      const onRoute = () => {
        const url = router.currentRoute?.url;
        if (url) setActivePath(url);
      };
      router.on('routeChanged', onRoute);
      onRoute();
      detach = () => router.off('routeChanged', onRoute);
    };
    attach();
    return () => {
      cancelled = true;
      detach();
    };
  }, [isDesktopLayout]);

  const activeLink = useMemo(() => {
    let best = '';
    for (const l of DETAIL_LINKS) {
      const match =
        activePath === l || activePath.startsWith(l + '/') || activePath.startsWith(l + '?');
      if (match && l.length > best.length) best = l;
    }
    return best;
  }, [activePath]);

  // Filter the left-rail nav by the search query: hide rows whose title doesn't
  // match, then hide any group (List) + its heading (BlockTitle) left empty.
  useEffect(() => {
    const root = navScrollRef.current;
    if (!root) return;
    const q = settingsQuery.trim().toLowerCase();
    root.querySelectorAll('li').forEach((li) => {
      const label = (li.querySelector('.item-title')?.textContent ?? li.textContent ?? '')
        .trim()
        .toLowerCase();
      (li as HTMLElement).style.display = !q || label.includes(q) ? '' : 'none';
    });
    root.querySelectorAll('.list').forEach((list) => {
      const anyVisible = Array.from(list.querySelectorAll('li')).some(
        (li) => (li as HTMLElement).style.display !== 'none',
      );
      (list as HTMLElement).style.display = !q || anyVisible ? '' : 'none';
    });
    root.querySelectorAll('.block-title').forEach((bt) => {
      let el = bt.nextElementSibling as HTMLElement | null;
      while (el && !el.classList.contains('list') && !el.classList.contains('block-title')) {
        el = el.nextElementSibling as HTMLElement | null;
      }
      const groupShown = !!el && el.classList.contains('list') && el.style.display !== 'none';
      (bt as HTMLElement).style.display = !q || groupShown ? '' : 'none';
    });
  }, [settingsQuery, activeLink, isDesktopLayout]);

  // Desktop only: load the page into the nested content view (section switch =
  // replace, no stacking). Mobile pushes via a declarative link instead (below).
  const openDetail = (path: string) => {
    const view = f7.views.get(`#${DETAIL_VIEW_ID}`);
    view?.router.navigate(path, { clearPreviousHistory: true, animate: false });
    setActivePath(path);
  };

  // Props for a settings nav row. On desktop we drive the nested view imperatively
  // (the row lives in the main view, so a declarative link would navigate the
  // wrong router). On mobile the row IS in the settings tab, so a plain
  // declarative `link` lets F7 push the page within that tab - the reliable path.
  const detailNav = (link: string) =>
    isDesktopLayout
      ? {
          link: '#',
          onClick: () => openDetail(link),
          className: activeLink === link ? 'settings-nav-active' : undefined,
        }
      : { link };

  // Open a health-banner fix page. When the issue names a parentRoute, push that
  // page first so the fix page's Back returns there (Location → business profile)
  // instead of dead-ending — the desktop rail otherwise clears history on a
  // section switch. Runs on whichever router owns the settings pages.
  const openHealthIssue = (issue: SettingsHealthIssue) => {
    // A top-level section (Menu) isn't a settings sub-page — swap the main view
    // to it (with a Back affordance), same as the settings Menu row does.
    if (issue.mainDestination) {
      navigateMain(issue.route, { animate: true });
      return;
    }
    if (isDesktopLayout) {
      const router = f7.views.get(`#${DETAIL_VIEW_ID}`)?.router;
      if (!router) return;
      if (issue.parentRoute) {
        router.navigate(issue.parentRoute, { clearPreviousHistory: true, animate: false });
        setActivePath(issue.parentRoute);
        router.navigate(issue.route);
      } else {
        openDetail(issue.route);
      }
    } else {
      const router = f7.views.current?.router;
      if (!router) return;
      if (issue.parentRoute) router.navigate(issue.parentRoute, { animate: false });
      router.navigate(issue.route);
    }
  };

  // Switch restaurant in place (not the full clear-and-re-pick flow). Desktop: an
  // anchored popover on the row. Phone: a full pushed page — the bottom sheet
  // couldn't scroll its long, franchise-grouped list reliably.
  const handleSwitchRestaurant = (el: HTMLElement) => {
    if (isDesktopLayout) {
      switcherAnchorRef.current = el;
      setSwitcherOpen(true);
      return;
    }
    f7.views.current?.router.navigate('/settings/switch-restaurant', { animate: true });
  };

  // Language picker - an action menu (same popup idiom as the order-availability
  // Pickup/Delivery menu), anchored to the row's globe icon so it pops on the
  // left. On wide viewports the global convertToPopover turns it into a popover.
  const openLanguagePicker = () => {
    f7.actions
      .create({
        targetEl: langAnchorRef.current ?? undefined,
        // One group (label + locales + Cancel) rather than a separate Cancel
        // group: the inter-group margin renders as an empty gap above Cancel,
        // which looks broken in the converted popover. Keeping Cancel as the
        // last row closes that gap.
        buttons: [
          [
            { label: true, text: t.settings.language },
            ...LOCALES.map((loc) => ({
              text: `${loc.native} - ${loc.label}`,
              bold: loc.value === locale,
              onClick: () => setLocale(loc.value),
            })),
            { text: t.cancel, color: 'red' },
          ],
        ],
      })
      .open();
  };

  // The currently selected restaurant, resolved against the user's franchises -
  // drives the name on the profile card at the top.
  const currentRestaurant = useMemo(() => {
    const workers = (ctx ?? storeContext)?.workers ?? [];
    for (const w of workers) {
      if (selectedFranchiseId && w.franchiseId !== selectedFranchiseId) continue;
      const r = w.restaurants.find((x) => x.restaurantId === selectedRestaurantId);
      if (r) return { name: r.restaurantName };
    }
    return null;
  }, [ctx, storeContext, selectedFranchiseId, selectedRestaurantId]);

  const restaurantName = restaurantConfig?.name ?? currentRestaurant?.name ?? t.settings.restaurant;

  const openStatus = useRestaurantOpenStatus();

  const { issues: allHealthIssues } = useSettingsHealth();
  const healthIssues = canManageOrderSetup
    ? allHealthIssues
    : allHealthIssues.filter((issue) => issue.route !== '/settings/orders');

  const handleLogout = () => {
    f7.dialog.confirm(t.signOutConfirm, t.signOut, async () => {
      // Detach this device's push token while the JWT is STILL valid — logout()
      // wipes it, so the authenticated DELETE must fire first or it 401s and the
      // token leaks to the signed-out account. Best-effort: never block sign-out.
      await unregisterPush();
      logout();
      clearRestaurant();
      f7.views.main.router.navigate('/login', { reloadAll: true });
    });
  };

  const sf = t.settingsFeature;
  const [shareUsageData, setShareUsageData] = useState(!isAnalyticsOptedOut());

  // Mobile-only quick-action grid pinned to the top of Settings: the sections
  // owners reach for most, one tap away instead of scrolling the nav list.
  const quickActions: {
    key: string;
    label: string;
    icon: string;
    color: string;
    go: () => void;
  }[] = [
    {
      key: 'menu',
      label: t.settings.menus,
      icon: 'square_list',
      color: '#f97316',
      go: () => navigateMain('/menu', { animate: true }),
    },
    {
      key: 'holidays',
      label: sf.holidays,
      icon: 'calendar_badge_minus',
      color: '#ef4444',
      go: () => f7.views.current?.router.navigate('/settings/holidays', { animate: true }),
    },
    {
      key: 'reports',
      label: sf.reports,
      icon: 'chart_bar_alt_fill',
      color: '#30b0c7',
      go: () => navigateMain('/reports', { animate: true }),
    },
    {
      key: 'promotions',
      label: sf.promotions,
      icon: 'tag',
      color: '#ec4899',
      go: () => navigateMain('/promotions', { animate: true }),
    },
    {
      key: 'customers',
      label: sf.allCustomers,
      icon: 'person_2',
      color: '#a855f7',
      go: () => navigateMain('/customers', { animate: true }),
    },
  ];

  const quickGrid = (
    <>
      <BlockTitle>{sf.features}</BlockTitle>
      <div className="settings-quick-grid">
        {quickActions.map((a) => (
          <button key={a.key} type="button" className="settings-quick-tile" onClick={a.go}>
            <SettingIcon icon={a.icon} color={a.color} size={48} />
            <span className="settings-quick-tile-label">{a.label}</span>
          </button>
        ))}
      </div>
    </>
  );
  // The settings nav - shared by the desktop left rail and the mobile list.
  const nav = (
    <>
      {/* iCloud-style profile card: identifies the active restaurant and is the
          tap target to switch between the franchises the user can access. */}
      <List strongIos strongMd insetIos insetMd className="restaurant-profile-list">
        <ListItem
          link="#"
          mediaItem
          chevronCenter
          className="restaurant-profile-card"
          onClick={(e: { currentTarget: HTMLElement }) => handleSwitchRestaurant(e.currentTarget)}
        >
          {restaurantConfig?.logoKey && (
            <div slot="media" className="restaurant-profile-avatar">
              <img src={mediaSrc(restaurantConfig.logoKey, 'small')} alt="" />
            </div>
          )}
          <div slot="title" className="restaurant-profile-name">
            {restaurantName}
          </div>
          {openStatus && (
            <div
              slot="subtitle"
              className={`restaurant-status ${openStatus.isOpen ? 'is-open' : 'is-closed'}`}
            >
              <span className="restaurant-status-dot" />
              {openStatus.label}
            </div>
          )}
        </ListItem>
      </List>

      <SettingsHealthBanner issues={healthIssues} onOpen={openHealthIssue} />

      {/* Sandbox warning — same amber "needs attention" idiom as the setup-health
          banner above, tapping it deep-links to the Payments page to switch live.
          Shown only while the storefront's active credentials are the sandbox set. */}
      {activePayMode === 'SANDBOX' && (
        <div className="settings-health">
          <List
            strongIos
            strongMd
            insetIos
            insetMd
            dividersIos
            dividersMd
            className="settings-health-list"
          >
            <ListItem
              title={t.payments.sandboxBannerTitle}
              text={t.payments.sandboxBannerBody}
              {...detailNav('/settings/payments')}
            >
              <div slot="media">
                <SettingIcon icon="exclamationmark_triangle_fill" color="#f59e0b" />
              </div>
            </ListItem>
          </List>
        </div>
      )}

      {!isDesktopLayout && quickGrid}

      <BlockTitle>{t.settings.restaurant}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.settings.restaurantSetup} {...detailNav('/settings/restaurant')}>
          <div slot="media">
            <SettingIcon icon="building_2" color="#64748b" />
          </div>
        </ListItem>
        <ListItem title={sf.openingHours} {...detailNav('/settings/opening-hours')}>
          <div slot="media">
            <SettingIcon icon="clock" color="#0ea5e9" />
          </div>
        </ListItem>
        {isDesktopLayout && (
          <ListItem title={sf.holidays} {...detailNav('/settings/holidays')}>
            <div slot="media">
              <SettingIcon icon="calendar_badge_minus" color="#ef4444" />
            </div>
          </ListItem>
        )}
        <ListItem title={t.settings.tablePlan} {...detailNav('/settings/reservation')}>
          <div slot="media">
            <SettingIcon icon="square_grid_2x2" color="#3b82f6" />
          </div>
        </ListItem>
      </List>

      <BlockTitle>{t.settings.storefront}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={sf.branding} {...detailNav('/settings/branding')}>
          <div slot="media">
            <SettingIcon icon="paintbrush" color="#ec4899" />
          </div>
        </ListItem>
        <ListItem title={sf.storefrontLink} {...detailNav('/settings/storefront-link')}>
          <div slot="media">
            <SettingIcon icon="link" color="#0ea5e9" />
          </div>
        </ListItem>
      </List>

      {featureFlags.inbox && (
        <>
          <BlockTitle>{sf.notifications}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem
              title={sf.inbox}
              link="#"
              onClick={() => setInboxOpen(true)}
              after={unreadCount > 0 ? sf.inboxNewCount(unreadCount) : undefined}
            >
              <div slot="media">
                <SettingIcon icon="bell" color="#ef4444" />
              </div>
            </ListItem>
          </List>
        </>
      )}

      <BlockTitle>{t.settings.reservations}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={sf.reservationSetup} {...detailNav('/settings/reservation/setup')}>
          <div slot="media">
            <SettingIcon icon="slider_horizontal_3" color="#6366f1" />
          </div>
        </ListItem>
        <ListItem title={t.settings.capacity} {...detailNav('/settings/reservation/shifts')}>
          <div slot="media">
            <SettingIcon icon="gauge" color="#14b8a6" />
          </div>
        </ListItem>
        <ListItem title={sf.reservationHours} {...detailNav('/settings/reservation/hours')}>
          <div slot="media">
            <SettingIcon icon="clock" color="#0ea5e9" />
          </div>
        </ListItem>
      </List>

      {isDesktopLayout && (featureFlags.settingsMenu || featureFlags.settingsCustomers) && (
        <>
          <BlockTitle>{t.settings.myRestaurant}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            {featureFlags.settingsMenu && (
              <ListItem
                title={t.settings.menus}
                link="#"
                onClick={() => navigateMain('/menu', { animate: true })}
              >
                <div slot="media">
                  <SettingIcon icon="square_list" color="#f97316" />
                </div>
              </ListItem>
            )}
            {featureFlags.settingsCustomers && (
              <ListItem
                title={sf.allCustomers}
                link="#"
                onClick={() => navigateMain('/customers', { animate: true })}
              >
                <div slot="media">
                  <SettingIcon icon="person_2" color="#a855f7" />
                </div>
              </ListItem>
            )}
          </List>
        </>
      )}

      {(featureFlags.settingsOnlineOrders || featureFlags.settingsStaff) && (
        <>
          <BlockTitle>{sf.onlineOrders}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            {/* Array (not a Fragment) so F7's <List> flattens these into its
                <ul> - a Fragment renders outside the ul, leaking the default
                <li> bullet + dropping the inset-card look. See FRONTEND_PRACTICES. */}
            {featureFlags.settingsOnlineOrders && [
              canManageOrderSetup && (
                <ListItem key="orders" title={sf.orderSetup} {...detailNav('/settings/orders')}>
                  <div slot="media">
                    <SettingIcon icon="bag" color="#34c759" />
                  </div>
                </ListItem>
              ),
              canManageOrderShifts && (
                <ListItem
                  key="shifts"
                  title={sf.orderShifts}
                  {...detailNav('/settings/orders/shifts')}
                >
                  <div slot="media">
                    <SettingIcon icon="gauge" color="#14b8a6" />
                  </div>
                </ListItem>
              ),
              canManageOpeningHours && (
                <ListItem
                  key="hours"
                  title={sf.orderHours}
                  {...detailNav('/settings/orders/hours')}
                >
                  <div slot="media">
                    <SettingIcon icon="clock" color="#0ea5e9" />
                  </div>
                </ListItem>
              ),
              <ListItem key="payments" title={sf.payments} {...detailNav('/settings/payments')}>
                <div slot="media">
                  <SettingIcon icon="creditcard" color="#34c759" />
                </div>
                {activePayMode && (
                  <span
                    slot="after"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: 19,
                      padding: '0 8px',
                      borderRadius: 9.5,
                      background: activePayMode === 'SANDBOX' ? '#ff3b30' : '#34c759',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {activePayMode === 'SANDBOX' ? t.payments.modeTest : t.payments.modeLive}
                  </span>
                )}
              </ListItem>,
              <ListItem key="labels" title={sf.labels} {...detailNav('/settings/labels')}>
                <div slot="media">
                  <SettingIcon icon="tag_fill" color="#f59e0b" />
                </div>
              </ListItem>,
              featureFlags.purge && canManagePurge && (
                <ListItem key="tidy-up" title={sf.tidyUp} {...detailNav('/settings/tidy-up')}>
                  <div slot="media">
                    <SettingIcon icon="sparkles" color="#34c759" />
                  </div>
                </ListItem>
              ),
              featureFlags.loyalty && (
                <ListItem key="loyalty" title={sf.loyalty} {...detailNav('/settings/loyalty')}>
                  <div slot="media">
                    <SettingIcon icon="star_fill" color="#f59e0b" />
                  </div>
                </ListItem>
              ),
              featureFlags.settingsGiftCards && (
                <ListItem
                  key="giftcards"
                  title={sf.giftCards}
                  {...detailNav('/settings/gift-cards')}
                >
                  <div slot="media">
                    <SettingIcon icon="creditcard_fill" color="#8b5cf6" />
                  </div>
                </ListItem>
              ),
            ]}
            {featureFlags.settingsStaff && (
              <ListItem title={sf.staff} {...detailNav('/settings/staff')}>
                <div slot="media">
                  <SettingIcon icon="person_2_fill" color="#0ea5e9" />
                </div>
              </ListItem>
            )}
          </List>
        </>
      )}

      {featureFlags.settingsTableOrdering && (
        <>
          <BlockTitle>{sf.tableOrdering}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={sf.dineInSetup} {...detailNav('/settings/table-ordering')}>
              <div slot="media">
                <SettingIcon icon="square_grid_2x2" color="#0d9488" />
              </div>
            </ListItem>
          </List>
        </>
      )}

      {featureFlags.settingsPrinting && (
        <>
          <BlockTitle>{sf.printing}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={sf.printers} {...detailNav('/settings/printers')}>
              <div slot="media">
                <SettingIcon icon="printer" color="#64748b" />
              </div>
            </ListItem>
          </List>
        </>
      )}

      {/* One "Notifications" group - push + email are two channels of the same
          thing. Previously they were separate sections whose headers collided
          (both t.settings.notifications and t.settings.mail.title render as
          "Notifications" / "Thông báo"), showing two identical one-row headers. */}
      <BlockTitle>{t.settings.notifications}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.settings.pushNotifications} {...detailNav('/settings/notifications')}>
          <div slot="media">
            <SettingIcon icon="bell" color="#ff453a" />
          </div>
        </ListItem>
        <ListItem title={t.settings.email} {...detailNav('/settings/mail')}>
          <div slot="media">
            <SettingIcon icon="envelope_fill" color="#34c759" />
          </div>
        </ListItem>
      </List>

      {/* Who changed what, across every settings screen. Franchise
          OWNER/MANAGER only — the server returns 403 for everyone else. */}
      <BlockTitle>{t.settingHistory.title}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.settingHistory.navTitle} {...detailNav('/settings/history')}>
          <div slot="media">
            <SettingIcon icon="clock_fill" color="#5856d6" />
          </div>
        </ListItem>
      </List>

      <BlockTitle>{t.settings.account}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {/* Email lives on its own line (subtitle) instead of the cramped `after`
            slot, so a long address gets the full row width before it truncates. */}
        <ListItem
          mediaItem
          chevronCenter
          title={sf.myProfile}
          subtitle={email ?? '-'}
          {...detailNav('/settings/profile')}
        >
          <div slot="media">
            <SettingIcon icon="person_circle" color="#8e8e93" />
          </div>
        </ListItem>
        <ListItem
          title={t.settings.language}
          after={LOCALES.find((l) => l.value === locale)?.native}
          link="#"
          onClick={(e) => {
            e.preventDefault();
            openLanguagePicker();
          }}
        >
          <div
            slot="media"
            ref={(el) => {
              langAnchorRef.current = el;
            }}
          >
            <SettingIcon icon="globe" color="#0ea5e9" />
          </div>
        </ListItem>
      </List>

      <BlockTitle>{sf.privacy}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={sf.shareUsageData} subtitle={sf.shareUsageDataHint}>
          <div slot="media">
            <SettingIcon icon="chart_bar" color="#5856d6" />
          </div>
          <label className="toggle" slot="after">
            <input
              type="checkbox"
              checked={shareUsageData}
              onChange={(e) => {
                setShareUsageData(e.target.checked);
                setAnalyticsOptOut(!e.target.checked);
              }}
            />
            <span className="toggle-icon" />
          </label>
        </ListItem>
      </List>

      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.signOut} link="#" onClick={handleLogout} style={{ color: '#ff3b30' }}>
          <div slot="media">
            <SettingIcon icon="square_arrow_left" color="#ff3b30" />
          </div>
        </ListItem>
      </List>

      <CompanyCredit />
      <AppVersionFooter />
    </>
  );

  const sheets = (
    <>
      {/* Desktop only: the anchored popover. Phones push /settings/switch-restaurant. */}
      {isDesktopLayout && (
        <RestaurantSwitcherSheet
          open={switcherOpen}
          onClose={() => setSwitcherOpen(false)}
          anchorEl={switcherAnchorRef.current}
        />
      )}
      {featureFlags.inbox && <InboxSheet open={inboxOpen} onClose={() => setInboxOpen(false)} />}
    </>
  );

  // ── Desktop: full-page shell - left nav + nested content view ──────────────
  if (isDesktopLayout) {
    return (
      <Page name="settings" pageContent={false} className="settings-fullpage">
        <div className="settings-fullpage-body">
          <aside className="settings-nav">
            <div className={`settings-nav-header${navScrolled ? ' is-scrolled' : ''}`}>
              <F7Link
                className="settings-nav-back"
                iconF7="chevron_left"
                aria-label={t.settings.dashboard}
                onClick={() => useUiStore.getState().setSettingsOpen(false)}
              />
              <h1 className="settings-nav-title">{t.settings.title}</h1>
            </div>
            <div
              ref={navScrollRef}
              className="settings-nav-scroll"
              onScroll={(e) => setNavScrolled(e.currentTarget.scrollTop > 0)}
            >
              {nav}
            </div>
            <div className="settings-nav-search">
              <i className="f7-icons settings-nav-search-icon">search</i>
              <input
                type="text"
                className="settings-nav-search-input"
                placeholder={t.settings.searchSettings}
                value={settingsQuery}
                onChange={(e) => setSettingsQuery(e.target.value)}
              />
              {settingsQuery && (
                <button
                  type="button"
                  className="settings-nav-search-clear"
                  aria-label={t.settings.searchSettings}
                  onClick={() => setSettingsQuery('')}
                />
              )}
            </div>
          </aside>
          <div className="settings-detail-host">
            <View id={DETAIL_VIEW_ID} name="settings-detail" url={DEFAULT_DETAIL} />
          </div>
        </div>
        {sheets}
      </Page>
    );
  }

  // ── Mobile: a plain list; sub-pages push on the settings tab ───────────────
  return (
    <Page name="settings">
      <Navbar title={t.settings.title} />
      {nav}
      {sheets}
    </Page>
  );
}

export default SettingsPage;
